import { LiveMap, Room } from "@liveblocks/client";
import { useRoom } from "@liveblocks/react";
import { captureEvent, captureException, Severity } from "@sentry/react";
import { useAtom } from "jotai";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { currentSpaceAtom } from "../jotai/atoms";
import { UpsertBlock, useBlocks, useSpaceInfo } from "../liveblocks/maps";
import { AnyBlock, Blocks, RoomStorage, Space } from "../types";
import friendlyWords from "friendly-words";
import { randomItem } from "../utils/randomItem";

/**
 * when users create a new space directly with URL (such as /new or /space/ID),
 * they can create the space with initial data: space name and template.
 * These initial data is supplied by URL query
 *
 * /space/ID?name=NAME&template=TEMPLATE_ID
 */
export function InitParamLoader({ children }: { children: React.ReactNode }) {
  const room = useRoom();

  const { upsertBlock, isReady } = useBlocks();
  const router = useRouter();
  const [inited, setInited] = useState(false);
  const [currentSpace] = useAtom(currentSpaceAtom);
  const [info, infoClient] = useSpaceInfo();

  useEffect(() => {
    if (!router.isReady || !isReady || !upsertBlock || inited) return;

    const { template } = router.query;
    if (typeof template === "string") {
      initStarter(room, upsertBlock, template)
        .catch((e) => captureException(e))
        .finally(() => {
          setInited(true);
        });
    } else {
      setInited(true);
    }
  }, [room, inited, router, upsertBlock, isReady]);

  // the effect give a initial space name when the space is loaded with
  // initial value query "space/{id}?spaceName=foo"
  useEffect(() => {
    if (!infoClient || !currentSpace || info?.name) {
      return;
    }

    const spaceName =
      typeof router.query === "string" ? router.query : friendlySpaceName();

    infoClient.set("name", spaceName);

    fetch("/api/space/upsert", {
      method: "POST",
      body: JSON.stringify({
        id: currentSpace.id,
        name: spaceName,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }).catch((e) => console.error(e));
  });

  return inited ? <>{children}</> : null;
}

export async function fetchSpaceData(id: Space["id"]): Promise<Blocks> {
  const res = await fetch(`/api/space/data?spaceId=${id}`);

  if (res.status !== 200) {
    captureEvent({
      message: "/api/space/data API error response",
      level: Severity.Error,
      extra: { ...res },
    });
    throw new Error("/api/space/data API error response");
  }

  const data = (await res.json()) as RoomStorage;
  if (!data.blocks) {
    throw new Error("unable to fetch template data");
  }

  return data.blocks;
}

async function initStarter(
  room: Room,
  upsertBlock: UpsertBlock,
  template: string
) {
  const { root } = await room.getStorage<{
    blocks: LiveMap<string, AnyBlock>;
  }>();

  const b = await root.get("blocks");
  if (b && b.size > 0) return;

  const starter = await fetchSpaceData(template);
  importBlocks(starter, upsertBlock);
}

export function importBlocks(
  blocks: Record<AnyBlock["id"], AnyBlock>,
  upsertBlock: UpsertBlock
) {
  const bs = Object.values(blocks).map((b) => {
    const { id, madeBy, ...fields } = b; // eslint-disable-line @typescript-eslint/no-unused-vars
    return fields;
  });
  upsertBlock(bs);
}

function friendlySpaceName() {
  const { predicates, collections, objects, teams } = friendlyWords;
  const subject = randomItem([collections, objects, teams]);
  return randomItem(predicates) + " " + randomItem(subject);
}
